import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/apiConfig';

const ACCEPT = '.txt,.md,.json,.docx,.pptx,.pdf';

export default function ContextEnginePanel({ accountId, userId, campaigns, defaultCampaignId, onClose, embedded = false, canWrite = true }) {
  const [documents, setDocuments] = useState([]);
  const [scope, setScope] = useState(defaultCampaignId ? 'campaign' : 'advertiser');
  const [campaignId, setCampaignId] = useState(defaultCampaignId || '');
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const campaignNames = useMemo(() => new Map((campaigns || []).map((campaign) => [String(campaign.id), campaign.name])), [campaigns]);

  const load = async () => {
    const response = await api.get('/api/v2/context-engine/documents', { params: { accountId, userId } });
    setDocuments(response.data.documents || []);
  };

  useEffect(() => {
    load().catch((requestError) => setError(requestError?.response?.data?.error || 'Failed to load context'));
  }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (event) => {
    event.preventDefault();
    if (!canWrite || !file || (scope === 'campaign' && !campaignId)) return;
    setBusy(true);
    setError('');
    try {
      const body = new FormData();
      body.append('file', file);
      if (label.trim()) body.append('label', label.trim());
      if (scope === 'campaign') body.append('campaignId', campaignId);
      await api.post('/api/v2/context-engine/documents', body, {
        params: { accountId, userId },
      });
      setFile(null);
      setLabel('');
      const input = document.getElementById('context-engine-file');
      if (input) input.value = '';
      await load();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Failed to upload context');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (documentId) => {
    if (!canWrite) return;
    setBusy(true);
    setError('');
    try {
      await api.delete(`/api/v2/context-engine/documents/${documentId}`, { params: { accountId, userId } });
      await load();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Failed to remove context');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={embedded ? "ow-business-context" : "pg-context-backdrop"} role="presentation" onMouseDown={(event) => { if (!embedded && event.target === event.currentTarget) onClose(); }}>
      <section className={embedded ? "pg-context-panel ow-panel" : "pg-context-panel"} role={embedded ? "region" : "dialog"} aria-modal={embedded ? undefined : true} aria-labelledby="context-engine-title">
        <header className="pg-context-head">
          <div>
            <h2 id="context-engine-title">{embedded ? "Context files" : "Advertiser context"}</h2>
            <p>Upload account knowledge once, or attach material to a specific campaign. New Diagnostic, Performance, pacing, and Trigger runs read active files automatically. Uploading does not rerun existing analyses.</p>
          </div>
          {!embedded && <button type="button" className="pg-context-close" aria-label="Close context panel" onClick={onClose}>×</button>}
        </header>

        {canWrite && <form className="pg-context-form" onSubmit={submit}>
          <label>
            Scope
            <select value={scope} onChange={(event) => setScope(event.target.value)}>
              <option value="advertiser">This advertiser</option>
              <option value="campaign">A specific campaign</option>
            </select>
          </label>
          {scope === 'campaign' && (
            <label>
              Campaign
              <select value={campaignId} onChange={(event) => setCampaignId(event.target.value)} required>
                <option value="">Select campaign</option>
                {(campaigns || []).map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}
              </select>
            </label>
          )}
          <label>
            Label <span>(optional)</span>
            <input value={label} onChange={(event) => setLabel(event.target.value)} placeholder="e.g. 2026 brand strategy" />
          </label>
          <label className="pg-context-file-label">
            File
            <input id="context-engine-file" type="file" accept={ACCEPT} onChange={(event) => setFile(event.target.files?.[0] || null)} required />
          </label>
          <button className="pg-btn pg-btn-ai" type="submit" disabled={busy || !file || (scope === 'campaign' && !campaignId)}>
            {busy ? 'Saving…' : 'Upload context'}
          </button>
          <div className="pg-context-help">DOCX, PPTX, PDF, Markdown, text, or JSON · 20 MB max. Uploaded claims remain unconfirmed evidence.</div>
        </form>}

        {error && <div className="pg-context-error">{error}</div>}
        <div className="pg-context-list">
          <h3>Upload history <span>{documents.length}</span></h3>
          {documents.length === 0 ? <div className="pg-context-empty">No context files uploaded yet.</div> : [...documents].sort((a,b)=>String(b.uploadedAt).localeCompare(String(a.uploadedAt))).map((item) => (
            <div className="pg-context-row" key={item.id}>
              <div className="pg-context-row-main">
                <strong>{item.label || item.originalName}</strong>
                <p>{item.summary ? `Summary (source excerpts): ${item.summary}` : 'Summary unavailable for this older upload.'}</p>
                <span>{item.scope === 'campaign' ? campaignNames.get(String(item.campaignId)) || 'Campaign' : 'Advertiser-wide'} · {new Date(item.uploadedAt).toLocaleDateString()}</span>
              </div>
              <span>{item.archivedAt ? 'Archived · excluded from analyses' : 'Active'}</span>
              <span className="pg-pill pg-pill-gray">{item.originalName.split('.').pop()?.toUpperCase()}</span>
              {canWrite && !item.archivedAt && <button type="button" className="pg-context-remove" disabled={busy} onClick={() => remove(item.id)}>Archive</button>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
